import { useEffect, useId, useRef, useState } from 'react';
import { searchPlaces, type PlaceResult } from '../data/postcodes';
import { loadSearch, searchPubNames, type PubHit } from '../data/pubs';
import { cleanText } from '../lib/sanitise';

type Option =
  | { kind: 'place'; key: string; place: PlaceResult }
  | { kind: 'pub'; key: string; pub: PubHit; label: string; detail: string };

interface Props {
  near: () => { lat: number; lon: number } | undefined;
  onPlace(place: PlaceResult): void;
  onPub(pub: PubHit): void;
}

/** Postcode / place / pub-name search as an ARIA combobox. */
export function SearchBox({ near, onPlace, onPub }: Props) {
  const listId = useId();
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<Option[]>([]);
  const [active, setActive] = useState(-1);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  /** The text a chosen result put in the box: no need to search for it again. */
  const picked = useRef<string | null>(null);
  // Callers often pass a fresh `near` each render; reading it through a ref
  // keeps that from re-running (and re-opening) the search.
  const nearRef = useRef(near);
  useEffect(() => {
    nearRef.current = near;
  }, [near]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2 || query === picked.current) return;
    const controller = new AbortController();
    // Every pub's name lives in search.json, fetched the first time someone types.
    const pubSearch = loadSearch().catch(() => null);
    const timer = window.setTimeout(() => {
      setStatus('Searching…');
      const pubOptions = (search: Awaited<typeof pubSearch>): Option[] =>
        search
          ? searchPubNames(search, q, nearRef.current(), 5).map((pub) => ({
              kind: 'pub' as const,
              key: `pub-${pub.id}`,
              pub,
              label: cleanText(pub.name, 60),
              detail: 'Pub',
            }))
          : [];
      Promise.all([pubSearch.then(pubOptions), searchPlaces(q, controller.signal)])
        .then(([pubs, places]) => {
          if (controller.signal.aborted) return;
          const next: Option[] = [
            ...places.map((place, i) => ({ kind: 'place' as const, key: `place-${i}`, place })),
            ...pubs,
          ];
          setOptions(next);
          setActive(next.length ? 0 : -1);
          setOpen(true);
          setStatus(next.length ? `${next.length} results` : 'No matches. Try a postcode or town.');
        })
        .catch(async (error: unknown) => {
          if (controller.signal.aborted) return;
          const pubs = pubOptions(await pubSearch);
          if (controller.signal.aborted) return;
          setOptions(pubs);
          setOpen(true);
          setStatus(
            pubs.length
              ? `${pubs.length} pubs (place search unavailable)`
              : `Search failed: ${error instanceof Error ? error.message : 'try again'}`,
          );
        });
    }, 250);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [query]);

  // Too-short queries show nothing, without clearing state in an effect.
  const searching = query.trim().length >= 2;
  const shown = searching ? options : [];
  const shownStatus = searching ? status : '';

  const choose = (option: Option) => {
    const label = option.kind === 'place' ? option.place.label : option.label;
    picked.current = label;
    setOpen(false);
    setQuery(label);
    inputRef.current?.blur();
    if (option.kind === 'place') onPlace(option.place);
    else onPub(option.pub);
  };

  return (
    <div className="search" role="search">
      <label htmlFor={`${listId}-input`} className="visually-hidden">
        Search by postcode, town or pub name
      </label>
      <input
        ref={inputRef}
        id={`${listId}-input`}
        className="search__input"
        type="search"
        inputMode="search"
        autoComplete="off"
        spellCheck={false}
        placeholder="Postcode, town or pub"
        role="combobox"
        aria-expanded={open && shown.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        value={query}
        onChange={(event) => {
          picked.current = null;
          setQuery(event.target.value);
        }}
        onFocus={() => shown.length > 0 && setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
            setActive((a) => Math.min(shown.length - 1, a + 1));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((a) => Math.max(0, a - 1));
          } else if (event.key === 'Enter') {
            const option = shown[active];
            if (option) {
              event.preventDefault();
              choose(option);
            }
          } else if (event.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {open && shown.length > 0 && (
        <ul id={listId} role="listbox" className="search__list" aria-label="Search results">
          {shown.map((option, i) => (
            <li
              key={option.key}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              className={`search__option${i === active ? ' is-active' : ''}`}
              onPointerDown={(event) => event.preventDefault()}
              onClick={() => choose(option)}
            >
              <span className="search__icon" aria-hidden="true">
                {option.kind === 'place' ? '📍' : '🍺'}
              </span>
              <span>
                <span className="search__label">
                  {option.kind === 'place' ? option.place.label : option.label}
                </span>
                <span className="search__detail">
                  {option.kind === 'place' ? option.place.detail : option.detail}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
      <span className="visually-hidden" role="status" aria-live="polite">
        {shownStatus}
      </span>
    </div>
  );
}
